/*
* Copyright (C) Regione Lombardia
*/
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { doDownload } from '@core/utilities';
import { environment } from '@env/environment';
import {
    DocumentoProgetto,
    LeggiProgetto,
    Progetto,
    RequestProgettoSearch,
    ResponseProgetto
} from '@models';
import { Observable, tap } from 'rxjs';
import { UtilsService } from './utils.service';
import { DettaglioCup } from 'src/app/_models/domain/dettaglio-cup';

@Injectable({
    providedIn: 'root'
})
export class ProgettoService {

    private _progetto: Progetto;
    get progettoSession() { return this._progetto; }
    set progettoSession(progetto: Progetto) { this._progetto = progetto; }

    constructor(
        private httpClient: HttpClient,
        private fb: FormBuilder,
        private utilsService: UtilsService
    ) { }

    public generateFormProgetto(values?: Progetto): FormGroup {
        const criteriaForm = this.fb.group({
            idProgetto: this.fb.control(null),
            idStato: this.fb.control(null),
            denominazione: this.fb.control(null, Validators.required),
            descrizione: this.fb.control(null),
            sigla: this.fb.control(null),
            studio: this.fb.control(null),
            impresa: this.fb.control(null),
            importoEuro: this.fb.control(null),
            amministrazioneAggiudicatrice: this.fb.control(null),
            cup: this.fb.control(null, Validators.required),
            codiceUnivoco: this.fb.control(null),
            vincoliPai: this.fb.control(null),
            accessoWebLimitato: this.fb.control(null),
            annoEsecuzione: this.fb.control(null,[Validators.pattern('^(19|20)[0-9]{2}$')]),
            note: this.fb.control(null),
        });
        if (values) {
            criteriaForm.patchValue(values);
        }
        return criteriaForm;
    }

    /* Ricerca */
    public search(values: RequestProgettoSearch, paginatorQueryParams: string): Observable<ResponseProgetto> {
        return this.httpClient.post<ResponseProgetto>(`${environment.urlApi}/private/progetto/ricerca?${paginatorQueryParams}`, values);
    }

    /* Progetto: CRUD */
    public create(params: Progetto): Observable<Progetto> {
        return this.httpClient.post<Progetto>(`${environment.urlApi}/private/progetto`, params);
    }
    public read(idProgetto: number): Observable<Progetto> {
        return this.httpClient.get<Progetto>(`${environment.urlApi}/private/progetto/${idProgetto}`);
    }
    public update(params: Progetto): Observable<Progetto> {
        return this.httpClient.put<Progetto>(`${environment.urlApi}/private/progetto`, params);
    }
    public delete(idProgetto: number) {
        return this.httpClient.delete(`${environment.urlApi}/private/progetto/${idProgetto}`, { observe: 'response' });
    }

    /* Informazioni aggiuntive */
    public documentoProgettoByIdProgetto(idProgetto: number): Observable<DocumentoProgetto[]> {
        return this.httpClient.get<DocumentoProgetto[]>(`${environment.urlApi}/private/progetto/${idProgetto}/documento-progetto`);
    }
    public leggiProgettoByIdProgetto(idProgetto: number): Observable<LeggiProgetto[]> {
        return this.httpClient.get<LeggiProgetto[]>(`${environment.urlApi}/private/progetto/${idProgetto}/legge-progetto`);
    }

    /* Leggi progetto: CRUD */
    public insertLeggiProgetto(params: LeggiProgetto): Observable<LeggiProgetto> {
        return this.httpClient.post<LeggiProgetto>(`${environment.urlApi}/private/legge-progetto`, params);
    }
    public updateLeggiProgetto(params: LeggiProgetto): Observable<LeggiProgetto> {
        return this.httpClient.put<LeggiProgetto>(`${environment.urlApi}/private/legge-progetto`, params);
    }
    public deleteLeggiProgetto(idLeggeProgetto: number) {
        return this.httpClient.delete(`${environment.urlApi}/private/legge-progetto/${idLeggeProgetto}`, { observe: 'response' });
    }

    /* Documento progetto: CRUD */
    public insertDocumento(file: any, idProgetto: number, documento: DocumentoProgetto): Observable<any> {
        const fileData = new FormData();
        fileData.append('file', file);
        fileData.append('didascalia', documento.didascalia);
        fileData.append('idClasseDocumento', documento.idClasseDocumento.toString());
        fileData.append('docPublic', documento.docPublic.toString());
        return this.httpClient.post<DocumentoProgetto>(`${environment.urlApi}/private/documento-progetto/upload/${idProgetto}`, fileData);
    }
    
    public updateDocumentoProgetto(documento: DocumentoProgetto): Observable<any> {
        const params = {
            idDocumento: documento.idDocumento,
            idClasseDocumento: documento.idClasseDocumento,
            didascalia: documento.didascalia,
            docPublic : documento.docPublic

        }
        return this.httpClient.put<DocumentoProgetto>(`${environment.urlApi}/private/documento-progetto`, params);
    }

    public deleteDocumento(idDocumentoProgetto: number): Observable<any> {
        return this.httpClient.delete<DocumentoProgetto>(`${environment.urlApi}/private/documento-progetto/${idDocumentoProgetto}`);
    }
    public downloadDocumento(item: DocumentoProgetto) {
        return this.httpClient.get(`${environment.urlApi}/private/documento-progetto/download/${item.idDocumento}`, {
            responseType: 'blob'
        }).pipe(
            tap((response) =>
                doDownload(
                    response,
                    item.nomeDocumento.substring(0, item.nomeDocumento.indexOf('.')),
                    item.nomeDocumento.substring(item.nomeDocumento.indexOf('.') + 1)
                )
            )
        );
    }

    public verificaCup(cup: string): Observable<any> {
        return this.httpClient.get<any>(`${environment.urlApi}/private/progetto/verifica-cup/${cup}`);
    }

    async isValidCUP(cup: string) {
        let isValid = false;
        await this.verificaCup(cup).toPromise().then(()=>{
            isValid = true;
        },
        err => {
            isValid = false;
        });
        return isValid;
    }

 /* Servizi CUP */
 public cupPost(codiceCup: String): Observable<DettaglioCup> {
        return this.httpClient.post<DettaglioCup>(`${environment.urlApi}/private/cup`, codiceCup);
    }

}
